import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { initializeApp } from 'firebase/app';
import { TokenService } from './core/services/token/token.service';
import { Storage } from '@ionic/storage-angular';
import { Device } from '@capacitor/device';
import { NavigationEnd, Router } from '@angular/router';
import { NavController, Platform, ToastController } from '@ionic/angular';
import { StorageService } from './core/services/storage/storage.service';
import { TokenJWT } from './core/models/token/token-jwt';
import { Subscription } from 'rxjs';
import { MessagingService } from './core/services/firebase/messaging.service';
import { RevenueCatService } from './core/services/revenue-cat/revenue-cat.service';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { FullScreenImageService } from './modules/full-screen-image/services/full-screen-image.service';
import { PosthogService } from './core/services/posthog/posthog.service';
import { StateAccountService } from './shared/services/state-account/state-account.service';
import { ChatService } from './shared/services/chat/chat.service';
import { ChatVeterinariaDataService } from './modules/chat-veterinaria/services/chat-veterinaria-data/chat-veterinaria-data.service';
import { SafeArea } from 'capacitor-plugin-safe-area';
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  token: TokenJWT | undefined;
  tokenSubscription$: Subscription = new Subscription();

  private readonly excludedUrls = ['/pet-diet',]; //elenco degli url che non richiedono l'accesso token

  // Deep link ricevuto ma non ancora applicabile: il flusso di avvio (update forzato,
  // login, guard GDPR) naviga per conto suo e passerebbe sopra la destinazione del link.
  private pendingDeepLink: string | null = null;
  private startupSettled = false;

  constructor(
    private router: Router,
    private zone: NgZone,
    private storageService: StorageService, // credo non si possa togliere perchè usa il create(), va invece implementato con le funzioni in modo da utilizzare poi il servizio nelle varie sezioni dell'app
    private storage: Storage,
    private tokenService: TokenService,
    private platform: Platform,
    private navCtrl: NavController,
    private messagingService: MessagingService,
    private rvnctService: RevenueCatService,
    private posthogService: PosthogService,
    private stateAccountService: StateAccountService,
    public fullscreenService: FullScreenImageService,
    private chatService: ChatService,
    private chatVetData: ChatVeterinariaDataService,
    private toastCtrl: ToastController,
  ) {
    if (!Capacitor.isNativePlatform()) {
      initializeApp(environment.firebase);
    }
  }

  async ngOnInit() {
    // Se siamo nel browser (non nell'app nativa) e c'è il parametro redirect=store,
    // redirect immediato allo store senza far partire l'app


    if (!Capacitor.isNativePlatform() && new URLSearchParams(window.location.search).get('redirect') === 'store') {
      const ua = navigator.userAgent || '';
      if (/android/i.test(ua)) {
        window.location.replace('https://play.google.com/store/apps/details?id=it.myvet&gl=IT');
      } else if (/iPad|iPhone|iPod/.test(ua)) {
        window.location.replace('https://apps.apple.com/it/app/myvet/id6463804287');
      } else {
        window.location.replace('https://myvet.it/');
      }
      return;
    }

    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => this.handleDeepLink(event.url));
    });

    // Avvio a freddo: se l'app viene lanciata da un link, appUrlOpen può essere già
    // stato emesso prima che questo listener esistesse. getLaunchUrl() è la rete di
    // sicurezza per non perdere la destinazione.
    App.getLaunchUrl()
      .then((launch) => {
        if (launch?.url) this.zone.run(() => this.handleDeepLink(launch.url));
      })
      .catch(() => {
        // piattaforme senza launch url (es. web): nessuna azione
      });

    if (await this.checkAppVersion()) {
      try {
        const jwtString = await this.storage.get('jwtTokenBusiness');
        const token = new TokenJWT(jwtString);
        this.tokenService.setToken(token);
        if (!token.isValid()) {
          this.logOut();
        }
      } catch (error) {
        console.error(error);
        this.logOut();
      }

      // ad OGNI avvio dell'app, non appena le risorse del telefono sono pronte
      this.platform.ready().then(async () => {
        this.saveUserDownload()
        this.rvnctService.init()
        this.setSafeArea()
        this.setupKeyboardListeners()
      });

      this.tokenSubscription$ = this.tokenService
        .getToken()
        .subscribe(async (token) => {
          if (token) {
            // Identifica l'utente su PostHog all'avvio dell'app
            this.identifyPostHog(token);

            this.saveUserAccess(token); // va messo per forza qua dentro perchè ha bisogno dell'id dell'account
            this.stateAccountService.upgradeStateAccount(token);
            // polling globale chat solo se abbonamento Chat Veterinaria attivo (IDStato = 1)
            this.startChatPollingIfSubscribed();
          } else {
            // logout: reset stato chat
            this.chatService.reset();
          }
          await this.checkSession(token);
        });

      // banner toast quando arriva un messaggio dal vet, da qualsiasi pagina
      this.chatService.messageReceived$.subscribe(async (received) => {
        if (!received) return;
        // non mostro il banner se l'utente è già nella chat
        if (this.router.url.includes('/chat')) return;
        const toast = await this.toastCtrl.create({
          message: 'Hai ricevuto un nuovo messaggio in chat',
          duration: 5000,
          position: 'top',
          cssClass: 'custom-toast-chat',
          buttons: [{ text: 'Apri', handler: () => this.router.navigate(['/chat']) }],
        });
        await toast.present();
      });
    } else {
      this.router.navigate(['/update']);
    }

    // Traccia ogni schermata visitata su PostHog
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const screenName = this.posthogService.screenNameFromUrl(event.urlAfterRedirects);
        if (screenName) this.posthogService.captureScreen(screenName);
      }
    });
  }

  ngOnDestroy() {
    if (this.tokenSubscription$) this.tokenSubscription$.unsubscribe();
  }

  /**
   * Avvia il polling chat solo se l'abbonamento Chat Veterinaria è attivo (IDStato = 1).
   * Se non attivo, niente polling per non sprecare chiamate al backend.
   */
  private async startChatPollingIfSubscribed() {
    const subscription = await this.chatVetData.readSubscription();
    if (subscription && subscription.IDStato === 1) {
      this.chatService.startBackgroundPolling(10000);
    } else {
      this.chatService.stopBackgroundPolling();
    }
  }

  async checkSession(token: TokenJWT | undefined) {

    //qui è inserito il codice per saltare il controllo del token, per poter accedere all'area Pet Diet
    const currentUrl = this.router.url;
    if (this.excludedUrls.some(url => currentUrl.startsWith(url))) {
      // Salta il controllo sessione per URL esclusi (per ora solo pet-diet)
      return;
    }
    //stop

    try {
      if (await this.isSessionAuthorized(token)) {
        await this.initNotifications(token!);
        this.settleStartup();
      } else {
        // sessione non valida: si va al login, il link non va rigiocato sopra
        this.pendingDeepLink = null;
        this.settleStartup();
        this.logOut();
        this.navCtrl.navigateRoot(['/login'], { replaceUrl: true });
      }
    } catch (error) {
      console.error(error);
      this.pendingDeepLink = null;
      this.settleStartup();
      this.logOut();
      this.navCtrl.navigateRoot(['/login'], { replaceUrl: true });
    }
  }

  /**
   * Registra il deep link in arrivo. Se l'avvio è già concluso naviga subito,
   * altrimenti resta in attesa e viene applicato da settleStartup().
   */
  private handleDeepLink(url: string) {
    const slug = this.deepLinkSlug(url);
    if (!slug) return;

    this.pendingDeepLink = slug;
    if (this.startupSettled) this.consumePendingDeepLink();
  }

  /**
   * Estrae il path dal deep link ignorando l'host: l'app intercetta sia
   * app.myvet.it che dev.app.myvet.it, ma environment.appUrl ne contiene uno solo.
   */
  private deepLinkSlug(url: string): string | null {
    try {
      const parsed = new URL(url);
      const slug = parsed.pathname + parsed.search + parsed.hash;
      return slug && slug !== '/' ? slug : null;
    } catch (error) {
      console.error('deep link: url non valido', url, error);
      return null;
    }
  }

  /** L'avvio ha finito di navigare: da qui in poi i deep link si applicano subito. */
  private settleStartup() {
    this.startupSettled = true;
    this.consumePendingDeepLink();
  }

  private consumePendingDeepLink() {
    const slug = this.pendingDeepLink;
    if (!slug) return;

    this.pendingDeepLink = null;
    this.router.navigateByUrl(slug);
  }

  async initNotifications(token: TokenJWT) {
    try {
      await this.messagingService.init();
      await this.registerNotificationToken(token);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Controlla i permessi dell'account
   */
  async isSessionAuthorized(token: TokenJWT | undefined): Promise<Boolean> {
    try {
      if (token === undefined) {
        throw new Error('Utente non loggato o sessione non recuperabile');
      }

      const response = await fetch(
        environment.apiUrl + environment.apiVersion + '/accounts/state/read',
        {
          headers: { Authorization: token.toString() },
          method: 'GET',
        }
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          `Account compromesso o non autorizzato, redirect: ${data.description}`
        );
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  async identifyPostHog(token: TokenJWT) {
    try {
      const payload = token.payload;
      const response = await fetch(
        environment.apiUrl + environment.apiVersion + '/accounts/read',
        { headers: { Authorization: token.toString() } }
      );
      const data = await response.json();

      if (response.ok && data.body) {
        this.posthogService.identify(payload.idAccount?.toString(), {
          email: data.body.email,
          name: `${data.body.nome} ${data.body.cognome}`.trim(),
          phone: data.body.telefono || undefined,
        });
      } else {
        this.posthogService.identify(payload.idAccount?.toString());
      }
    } catch (e) {
      console.warn('PostHog identify error:', e);
    }
  }

  async logOut() {
    this.posthogService.reset();
    this.messagingService.removeToken();
    await this.removeNotificationToken();

    await this.storage.remove('jwtTokenBusiness');
  }

  async registerNotificationToken(token: TokenJWT) {
    // Da dove prenderò le informazioni del dispositivo
    const id = await Device.getId();
    const info = await Device.getInfo();
    const params = JSON.stringify({
      Identificativo: id.identifier,
      SistemaOperativo: info.platform,
      Modello: info.model,
      Nome: info.name,
      TokenNotifiche: await this.messagingService.getToken(),
    });
    fetch(
      environment.apiUrl +
        environment.apiVersion +
        '/notifications/device/create',
      {
        headers: { Authorization: token.toString() },
        body: params,
        method: 'POST',
      }
    );
  }

  async removeNotificationToken() {
    const params = JSON.stringify({
      TokenNotifiche: await this.messagingService.getToken(),
    });
    fetch(
      environment.apiUrl +
        environment.apiVersion +
        '/notifications/device/delete',
      {
        body: params,
        method: 'POST',
      }
    );
  }

  /**
   * Salva sul DB un nuovo download dell'app in caso fosse il primo accesso
   */
  async saveUserDownload() {
    try {
      const isFirstAccessSaved = await this.storage.get('firstAccess');

      // se è già stato eseguito il primo accesso all'app non salvo nulla e interrompo
      if (isFirstAccessSaved) {
        return;
      }

      let sistemaOperativo = '';
      if (this.platform.is('ios')) sistemaOperativo = 'IOS';
      if (this.platform.is('android')) sistemaOperativo = 'Android';

      const info = await Device.getInfo();
      const versioneSistemaOperativo = info.osVersion;

      // mi salvo sul DB il download
      const params = JSON.stringify({
        App: 'Utente',
        SistemaOperativo: sistemaOperativo,
        VersioneSistemaOperativo: versioneSistemaOperativo,
      });
      fetch(
        environment.apiUrl + environment.apiVersion + '/accesses/first/create',
        {
          // non ci passo l'header perchè non cè bisogno dell'autorizzazione (non essendoci ancora il token)
          method: 'POST',
          body: params,
        }
      );

      // mi salvo il primo accesso all'app da parte dell'utente
      await this.storage.set('firstAccess', true);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Salva sul DB il nuovo accesso all'app da parte di un utente, e registra l'evento login sul Google Tag Manager
   */
  async saveUserAccess(token: TokenJWT) {
    try {
      fetch(environment.apiUrl + environment.apiVersion + '/accesses/create', {
        headers: { Authorization: token!.toString() },
      });
    } catch (error) {
      console.error(error);
    }


    // recupero gli animali associati all'utente, dovranno essere passati come parametro durante il tracciamento dell'evento login
    const animali: any[] = []
    try {
      const response = await fetch(
        environment.apiUrl + environment.apiVersion + '/animals/read-all',
        { headers: { Authorization: token!.toString() } }
      );

      const data = await response.json() // estrae il JSON dalla risposta
      if (response.ok && Array.isArray(data.body)) {
        data.body.forEach((pet: any) => {
          animali.push({
            idAnimale: pet.idAnimale,
            nome: pet.nome,
            dataNascita: pet.dataNascita,
            luogoNascita: pet.luogoNascita,
            sesso: pet.sesso,
            nomeTaglia: pet.nomeTaglia,
            peso: pet.peso,
            sterilizzato: pet.sterilizzato,
            assicurato: pet.assicurato,
            microchip: pet.microchip,
            passaporto: pet.passaporto,
            nomeSpecie: pet.nomeSpecie,
            nomeRazza: pet.nomeRazza,
            nomeAttivita: pet.nomeAttivita,
            nomeCorporatura: pet.nomeCorporatura,
            mantello: pet.mantello,
            descrizione: pet.descrizione,
          })
        })
      }
    } catch (e) {
      console.warn("Impossibile recuperare i dati dell'animale:", e);
    }

    // registro l'evento sul google tag manager (sia frontend che backend)
    this.posthogService.capture('login', { pets: animali })
  }

  /**
   * Controlla (interrogando il server) se l'app necessita di un aggiornamento obbligatorio
   * @returns TRUE se il server ha concesso il via libera
   * @returns FALSE se il server richiede che venga effettuato un aggiornamento
   */
  async checkAppVersion() {
    const params = new URLSearchParams({
      CodiceVersione: environment.appVersion,
    });
    try {
      let response = await fetch(
        environment.apiUrl +
          environment.apiVersion +
          `/versions/check-update?${params}`
      );

      let data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      // !!! ritorno direttamente il valore ricevuto dal server
      return data.body;
    } catch (error) {
      // in caso di errore dò sempre l'accesso all'utente
      return true;
    }
  }

  /**
   * Imposta le variabili CSS di safe area per gestire notch e barre di sistema su Android/iOS
   */
  setupKeyboardListeners() {
    window.addEventListener('keyboardWillShow', () => {
      document.body.classList.add('keyboard-is-open');
    });
    window.addEventListener('keyboardWillHide', () => {
      document.body.classList.remove('keyboard-is-open');
    });
  }

  async setSafeArea(){
    if (!this.platform.is('hybrid')) return; // niente plugin su browser
    // Android 15+: inset gestiti da Capacitor 8 (SystemBars → padding nativo o env()).
    // Android < 15: StatusBar overlaysWebView false (capacitor.config.ts) → la WebView parte già sotto la status bar
    if (this.platform.is('android')) return;

    try {
      const { insets } = await SafeArea.getSafeAreaInsets()

      // Su Android con schermi piccoli (< 700px), la navigation bar a 3 tasti
      // occupa già spazio fisico: il safe area bottom causa spazio bianco eccessivo
      // sotto la tab bar. Limitiamo il valore a un massimo ragionevole.
      let bottomInset = insets.bottom;
      if (this.platform.is('android') && window.innerHeight < 700) {
        bottomInset = 0;
      }

      document.body.style.setProperty("--ion-safe-area-top", `${insets.top}px`)
      document.body.style.setProperty("--ion-safe-area-right", `${insets.right}px`)
      document.body.style.setProperty("--ion-safe-area-bottom", `${bottomInset}px`)
      document.body.style.setProperty("--ion-safe-area-left", `${insets.left}px`)
    } catch (error) {
      console.error('SafeArea error', error)
    }
  }
}
